/**
 * Bitmask permission utilities for the frontend.
 *
 * Mirrors the backend permission system:
 *   CREATE = 1, READ = 2, WRITE = 4, DELETE = 8, IMPORT = 16
 *
 * Permissions JSONB: { "resource": bitmask, "*": 31 }
 */

export const ACTION_BITS: Record<string, number> = {
  create: 1,
  read: 2,
  write: 4,
  delete: 8,
  import: 16,
};

export type PermissionsBitmask = Record<string, number>;

/**
 * Check if a bitmask permissions object grants a specific permission code.
 * e.g., checkPermission({ events: 6, '*': 31 }, 'events:read') → true
 */
export function checkPermission(
  permissions: PermissionsBitmask | null | undefined,
  code: string,
): boolean {
  if (!permissions) return false;

  const [resource, action] = code.split(':');
  if (!resource || !action) return false;

  const actionBit = ACTION_BITS[action];
  if (actionBit === undefined) return false;

  // Check specific resource
  if ((permissions[resource] ?? 0) & actionBit) return true;

  // Check wildcard
  if ((permissions['*'] ?? 0) & actionBit) return true;

  return false;
}

/**
 * Check if a bitmask permissions object has the wildcard ('*') key.
 */
export function hasWildcard(permissions: PermissionsBitmask | null | undefined): boolean {
  if (!permissions) return false;
  return permissions['*'] !== undefined && permissions['*'] > 0;
}

/**
 * Convert a bitmask permissions object to an array of permission strings.
 * e.g., { events: 6, members: 2 } → ['events:read', 'events:write', 'members:read']
 */
export function bitmaskToStrings(permissions: PermissionsBitmask): string[] {
  const result: string[] = [];

  for (const [resource, bitmask] of Object.entries(permissions)) {
    if (resource === '*') {
      result.push('*');
      continue;
    }
    for (const [action, bit] of Object.entries(ACTION_BITS)) {
      if (bitmask & bit) {
        result.push(`${resource}:${action}`);
      }
    }
  }

  return result;
}

/**
 * Convert an array of permission strings to a bitmask permissions object.
 * e.g., ['events:read', 'events:write', 'members:read'] → { events: 6, members: 2 }
 */
export function stringsToBitmask(permissions: string[]): PermissionsBitmask {
  const result: PermissionsBitmask = {};

  for (const perm of permissions) {
    if (perm === '*') {
      result['*'] = 31; // all bits
      continue;
    }
    const [resource, action] = perm.split(':');
    if (resource && action && ACTION_BITS[action] !== undefined) {
      result[resource] = (result[resource] || 0) | ACTION_BITS[action]!;
    }
  }

  return result;
}
